import { ApiError, apiRequest, fetchAuthenticatedBlob } from './client';
import type { Payment } from '../types/admin';

export const PAYMENT_INVOICE_MAX_BYTES = 15 * 1024 * 1024;
export const PAYMENT_INVOICE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx';
/** Accept list for payments whose only requisite is the attached PDF invoice. */
export const PAYMENT_INVOICE_PDF_ACCEPT = '.pdf';

/**
 * How the patient settles a payment.
 *
 * `invoice_pdf` carries no bank account or link: the attached PDF invoice is
 * the requisite, so the server requires it to be a PDF and refuses to delete it.
 */
export type PaymentMethod = 'bank' | 'link' | 'invoice_pdf';

export function isPdfPaymentInvoiceFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return extension === 'pdf' && isSupportedPaymentInvoiceFile(file);
}

const PAYMENT_INVOICE_MIME_TYPES_BY_EXTENSION: Readonly<Record<string, readonly string[]>> = {
  pdf: ['application/pdf'],
  doc: ['application/msword'],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/zip',
    'application/x-zip-compressed',
  ],
  xls: ['application/vnd.ms-excel'],
  xlsx: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/zip',
    'application/x-zip-compressed',
  ],
};
const GENERIC_BINARY_MIME_TYPES = new Set(['', 'application/octet-stream', 'binary/octet-stream']);

export function isSupportedPaymentInvoiceFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const acceptedTypes = PAYMENT_INVOICE_MIME_TYPES_BY_EXTENSION[extension];
  return Boolean(
    acceptedTypes &&
      (acceptedTypes.includes(file.type.toLowerCase()) || GENERIC_BINARY_MIME_TYPES.has(file.type.toLowerCase())),
  );
}

export type CreatePatientPaymentPayload = {
  readonly title?: string | null;
  readonly payment_method?: PaymentMethod;
  readonly bank_account_id?: string;
  readonly payment_link?: string;
  readonly amount: number;
  readonly currency: string;
  readonly deadline?: string | null;
  readonly mark_paid?: boolean;
};

export type UpdatePatientPaymentPayload = {
  readonly payment_status?: 'confirmed' | 'rejected' | null;
  readonly rejection_reason?: string | null;
  readonly title?: string | null;
  readonly payment_method?: PaymentMethod;
  readonly bank_account_id?: string | null;
  readonly payment_link?: string | null;
  readonly amount?: number | null;
  readonly currency?: string | null;
  readonly deadline?: string | null;
};

export type AdminPatientPaymentResponse = Payment & {
  readonly payment_method?: PaymentMethod;
  readonly bank_account_id: string | null;
  readonly payment_link: string | null;
  readonly patient_paid_at: string | null;
  readonly confirmed_by_admin_id: string | null;
  readonly rejection_reason: string | null;
  readonly created_at: string;
  readonly updated_at: string;
};

export function listPatientPayments(
  patientId: string,
  accessToken: string,
): Promise<readonly AdminPatientPaymentResponse[]> {
  return apiRequest<readonly AdminPatientPaymentResponse[]>(
    `/admin/patients/${patientId}/payments`,
    { method: 'GET', token: accessToken },
  );
}

export function createPatientPayment(
  patientId: string,
  payload: CreatePatientPaymentPayload,
  accessToken: string,
): Promise<AdminPatientPaymentResponse> {
  return apiRequest<AdminPatientPaymentResponse>(
    `/admin/patients/${patientId}/payments`,
    { method: 'POST', body: payload, token: accessToken },
  );
}

export function updatePatientPayment(
  patientId: string,
  paymentId: string,
  payload: UpdatePatientPaymentPayload,
  accessToken: string,
): Promise<AdminPatientPaymentResponse> {
  return apiRequest<AdminPatientPaymentResponse>(
    `/admin/patients/${patientId}/payments/${paymentId}`,
    { method: 'PATCH', body: payload, token: accessToken },
  );
}

export function deletePatientPayment(
  patientId: string,
  paymentId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(
    `/admin/patients/${patientId}/payments/${paymentId}`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  );
}

export function setPatientPaymentInvoice(
  patientId: string,
  paymentId: string,
  file: File,
  accessToken: string,
): Promise<AdminPatientPaymentResponse> {
  if (!isSupportedPaymentInvoiceFile(file)) {
    return Promise.reject(new ApiError('Unsupported file type. Please upload a PDF, Word, or Excel file.', 415));
  }
  if (file.size > PAYMENT_INVOICE_MAX_BYTES) {
    return Promise.reject(new ApiError('Invoice file exceeds the 15 MB size limit.', 413));
  }

  const body = new FormData();
  body.append('file', file);
  return apiRequest<AdminPatientPaymentResponse>(
    `/admin/patients/${patientId}/payments/${paymentId}/invoice`,
    {
      method: 'POST',
      body,
      token: accessToken,
    },
  );
}

export function deletePatientPaymentInvoice(
  patientId: string,
  paymentId: string,
  accessToken: string,
): Promise<void> {
  return apiRequest<void>(
    `/admin/patients/${patientId}/payments/${paymentId}/invoice`,
    {
      method: 'DELETE',
      token: accessToken,
    },
  );
}

export function fetchPatientPaymentInvoiceBlob(
  patientId: string,
  paymentId: string,
  accessToken: string,
): Promise<Blob> {
  return fetchAuthenticatedBlob(
    `/admin/patients/${patientId}/payments/${paymentId}/invoice`,
    accessToken,
  );
}
